import { provideHttpClient, withFetch } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    EnvironmentProviders,
    importProvidersFrom,
    provideExperimentalZonelessChangeDetection,
    Provider,
    provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideDfApi } from '@df/api/df-api.providers';
import { provideDfBrowser } from '@df/browser/df-browser.providers';
import { provideDfCatalogUi } from '@df/catalog-ui/df-catalog-ui.providers';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { IsBannerPluginService } from '@df/catalog-ui/listing/plugin/banner/is-banner-plugin.service';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { ListingSort } from '@df/catalog-ui/listing/sort/listing-sort';
import { provideDfCatalogUiRecentlyViewed } from '@df/catalog-ui/recently-viewed/recently-viewed.providers';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { CustomformResource } from '@df/catalog/customform/customform.resource';
import { provideDfCatalog } from '@df/catalog/df-catalog.providers';
import { Product as CoreProduct } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { StockResource } from '@df/catalog/stock/stock.resource';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { provideDfCheckout } from '@df/checkout/df-checkout.providers';
import { provideDfCore } from '@df/core/df-core.providers';
import { StructuralDataService } from '@df/core/meta/structural-data/structural-data.service';
import { Client } from '@df/core/service/client';
import { provideDfDom } from '@df/dom/df-dom.providers';
import { V12RetailFinanceModule } from '@df/module-V12/v12-retail-finance.module';
import { provideDfModuleAb } from '@df/module-ab/ab.providers';
import { provideDfModuleAbCms2 } from '@df/module-ab/cms2/ab-cms2.providers';
import { provideDfModuleElasticsearch } from '@df/module-elasticsearch/elasticsearch.providers';
import { FacetUrlService } from '@df/module-facet-url/core/facet-url.service';
import { provideDfModuleFacetUrl } from '@df/module-facet-url/facet-url.providers';
import { FacetUrlMetaPluginService } from '@df/module-facet-url/plugin/meta.plugin.service';
import { provideDfModuleGiftcards } from '@df/module-giftcards/giftcards.providers';
import { provideDfModuleGoogleMaps } from '@df/module-google-maps/google-maps.providers';
import { provideDfModuleGtm } from '@df/module-gtm/gtm.providers';
import { GtmService } from '@df/module-gtm/gtm.service';
import { provideDfModuleImage } from '@df/module-image/image.providers';
import { provideDfModuleKlevuSearch } from '@df/module-klevu-search/klevu-search.providers';
import { ListingSortKlevuEngine } from '@df/module-klevu-search/listing-sort-klevu.engine';
import { provideDfModuleOneTrust } from '@df/module-onetrust/onetrust.providers';
import { DfModulePaypalModule } from '@df/module-paypal/df-module-paypal.module';
import { provideDfModulePca } from '@df/module-pca/pca.providers';
import { provideDfModulePrivacy } from '@df/module-privacy/privacy.providers';
import { DfThumborModule } from '@df/module-thumbor/df-thumbor.module';
import { DfSearchModule } from '@df/search/df-search.module';
import { SearchService } from '@df/search/search.service';
import { AddressBook } from '@df/session/api/address-book/address-book';
import { Basket } from '@df/session/api/basket/basket';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { provideDfSession } from '@df/session/df-session.providers';
import { provideDfUi } from '@df/ui/df-ui.providers';
import { ModalModule } from '@df/ui/modal/modal.module';
import { DfUniversalModule } from '@df/universal/client/df-universal.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import { provideAccount } from './account/account';
import { provideModals } from './app-modals';
import { appRoutes } from './app.routes';
import { LocalBasket } from './basket/basket.decorator';
import { LocalBasketService } from './basket/basket.service.decorator';
import './catalog/category/category.decorator';
import { LocalCategoryResource } from './catalog/category/category.decorator';
import { LocalICmsPage } from './catalog/page/local/local-page';
import { LocalPageResource } from './catalog/page/local/local-page.resource';
import './catalog/page/page.config';
import './catalog/product/product.decorator';
import { LocalProductResource, Product } from './catalog/product/product.decorator';
import { provideRelatedProductsEngines } from './catalog/product/related-products/related-products.module';
import './catalog/product/sale-banner/sale-banner.cms-config';
import { LocalStore, LocalStoreResource } from './catalog/store/store.decorator';
import { LocalFilterAttributeOption } from './catalog/ui/listing/filter/local-filter-attribute-option';
import { LocalListingSort } from './catalog/ui/listing/sort/local-listing-sort';
import { provideCmsComponents } from './cms/cms.module';
import { PDPComponentGuardModule } from './cms/pdp-component-guard/pdp-component-guard.module';
import { ICMS_CONFIG } from './icms.config';
import { LocalClient } from './module/core/client.decorator';
import { LocalFacetUrlService } from './module/facet-url/facet-url.service.decorator';
import { LocalFacetUrlMetaPluginService } from './module/facet-url/meta.plugin.service.decorator';
import { LocalGtmService } from './module/gtm/gtm.service.decorator';
import { LocalListingSortKlevuEngine } from './module/klevu-search/listing-sort-klevu.engine';
import { LocalStructuralDataService } from './module/meta/structural-data/structural-data.service.decorator';
import { ReviewsIOService } from './module/reviews-io/reviews-io.service';
import { LocalSamplesModule } from './module/samples/local-samples.module';
import { AudioEyeService } from './module/audioeye/audioeye.service';
import { SHELL_IMAGE } from './ui/display/local-shell.component';
import { ProductListingFactoryDecorator } from './ui/listing/product-listing.factory.decorator';

const providers: (Provider | EnvironmentProviders)[] = [];
if (typeof window !== 'undefined') {
    providers.push(provideExperimentalZonelessChangeDetection());
} else {
    providers.push(
        provideZoneChangeDetection({
            eventCoalescing: false
        })
    );
}
export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideHttpClient(withFetch()),
        provideAnimations(),
        provideDfApi(),
        provideDfBrowser(),
        provideDfCatalog(),
        provideDfCatalogUi({
            listing: {
                resetTop: 0,
                category: {
                    size: [
                        {
                            size: 1,
                            onSize: ['S', 'M']
                        },
                        {
                            size: 2,
                            onSize: ['S', 'M']
                        },
                        {
                            size: 3,
                            onSize: ['L', 'X']
                        },
                        {
                            size: 4,
                            onSize: ['L', 'X']
                        }
                    ],
                    defaultSize: [2, 2, 4, 4],
                    infScrollInit: [24, 24, 24, 24],
                    infScrollStep: [24, 24, 24, 24],
                    sort: [
                        {
                            label: 'Recommended',
                            default: true,
                            engine: 'klevu'
                        },
                        {
                            label: 'New',
                            code: 'created_at',
                            desc: true
                        },
                        {
                            label: 'Highest price',
                            code: 'price',
                            desc: true
                        },
                        {
                            label: 'Lowest price',
                            code: 'price'
                        }
                    ]
                }
            }
        }),

        provideDfCatalogUiRecentlyViewed(),
        provideDfCheckout({
            autoPickShippingMethod: false,
            autoPickShippingMethodIfOnlyOneAvailable: false,
            useCheckoutEndpoint: true
        }),
        provideDfCore({
            url: {
                pathRules: [
                    {
                        from: /^magestore\/storepickup\/images\/tag/,
                        to: '/static/media/magestore/storepickup/images/tag'
                    },
                    {
                        from: /^\/catalog\/plp_banner\//,
                        to: '/static/media/catalog/plp_banner/'
                    }
                ]
            }
        }),
        provideDfDom(),
        provideDfModuleAb({
            convertcom: {
                script: app =>
                    ({
                        uat: 'https://cdn-4.convertexperiments.com/js/10041560-10041748.js',
                        staging: 'https://cdn-4.convertexperiments.com/js/10041560-10041771.js',
                        production: 'https://cdn-4.convertexperiments.com/js/10041560-10041825.js'
                    })[app.dfConfig.environment]
            }
        }),
        provideDfModuleAbCms2(),
        provideDfModuleElasticsearch(),
        provideDfModuleFacetUrl(),
        provideDfModuleGiftcards(),
        provideDfModuleGoogleMaps({
            mapOptions: {
                zoom: 12,
                streetViewControl: true,
                scrollwheel: false
            },
            storeToMarker: (store: Store, data: google.maps.MarkerOptions) => {
                if (store.data) {
                    return Object.assign(data, {
                        icon: {
                            path: 'M0.5 6.3C0.5 2.817 3.317 0 6.8 0C10.283 0 13.1 2.817 13.1 6.3C13.1 11.025 6.8 18 6.8 18C6.8 18 0.5 11.025 0.5 6.3ZM4.55 6.3C4.55 7.542 5.558 8.55 6.8 8.55C8.042 8.55 9.05 7.542 9.05 6.3C9.05 5.058 8.042 4.05 6.8 4.05C5.558 4.05 4.55 5.058 4.55 6.3Z',
                            fillColor: '#1A2A49',
                            fillOpacity: 1,
                            strokeColor: '#1A2A49',
                            strokeWeight: 1,
                            scale: 1.7,
                            anchor: new google.maps.Point(7, 18)
                        },
                        storeId: store.id
                    });
                }
                return data;
            }
        }),
        provideDfModuleGtm(),
        provideDfModuleImage({
            placeholder: SHELL_IMAGE,
            placeholderBlur: false,
            placeholderBackgroundColor: 'transparent'
        }),
        provideDfModuleKlevuSearch(),
        provideDfModuleOneTrust(),
        provideDfModulePca(),
        provideDfModulePrivacy(),
        provideAccount(),
        provideCmsComponents(),
        provideDfSession(),
        provideRelatedProductsEngines(),
        ReviewsIOService,
        provideModals(),
        provideDfUi({
            price: {
                precision: 'auto'
            }
        }),
        importProvidersFrom(
            ICmsClientModule.forRoot(ICMS_CONFIG),
            DfThumborModule.forRoot({ defaultOptions: { quality: 75 } }),
            DfSearchModule.forRoot(),
            DfModulePaypalModule.forRoot({}),
            LocalSamplesModule.forRoot({ limit: 6 }),
            V12RetailFinanceModule,
            DfThumborModule,
            DfUniversalModule,
            ModalModule,
            PDPComponentGuardModule.forRoot()
        ),
        // Providers
        { provide: CategoryResource, useClass: LocalCategoryResource },
        { provide: StoreResource, useClass: LocalStoreResource },
        { provide: StructuralDataService, useClass: LocalStructuralDataService },
        { provide: Store, useClass: LocalStore },
        { provide: Basket, useClass: LocalBasket },
        { provide: GtmService, useClass: LocalGtmService },
        { provide: ProductListingFactory, useClass: ProductListingFactoryDecorator },
        { provide: FacetUrlService, useClass: LocalFacetUrlService },
        { provide: FacetUrlMetaPluginService, useClass: LocalFacetUrlMetaPluginService },
        { provide: ICmsPage, useClass: LocalICmsPage },
        { provide: ICmsPageResource, useClass: LocalPageResource },
        { provide: BasketService, useClass: LocalBasketService },
        { provide: CoreProduct, useClass: Product },
        { provide: ProductResource, useClass: LocalProductResource },
        { provide: FilterAttributeOption, useClass: LocalFilterAttributeOption },
        { provide: ListingSort, useClass: LocalListingSort },
        { provide: Client, useClass: LocalClient },
        { provide: ListingSortKlevuEngine, useClass: LocalListingSortKlevuEngine },
        {
            provide: APP_INITIALIZER,
            useFactory: (gtmService: GtmService, searchService: SearchService) => () => {},
            deps: [
                GtmService,
                SearchService,
                IsBannerPluginService,
                CustomformResource,
                AttributeResource,
                CategoryResource,
                ProductResource,
                StockResource,
                Wishlist,
                AddressBook,
                StoreResource,
                AudioEyeService
            ],
            multi: true
        },
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'disabled' // we have our own implementation that does that
            })
        )
    ]
};
